// VoteButton - US-3.2: Voting interface for cards
// Displays vote count and allows users to cast/remove votes

import React from 'react';
import { CircleDot } from 'lucide-react';
import styles from './VoteButton.module.css';

interface VoteButtonProps {
  cardId: string;
  voteCount: number;
  votingActive: boolean;
  dotsRemaining: number;
  onVote: (cardId: string, dots: number) => void;
}

export const VoteButton: React.FC<VoteButtonProps> = ({
  cardId,
  voteCount,
  votingActive,
  dotsRemaining,
  onVote,
}) => {
  // HANDLE vote click (add 1 dot)
  const handleVote = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expand/collapse
    if (dotsRemaining > 0) {
      onVote(cardId, 1);
    }
  };

  // RENDER vote badge with count
  if (!votingActive) {
    // SHOW vote count only (voting not active)
    if (voteCount === 0) {
      return null; // Hide if no votes
    }
    return (
      <div className={`${styles.voteBadge} ${styles.voteBadgeInactive}`}>
        <CircleDot size={14} className={styles.voteIcon} />
        <span className={styles.voteCount}>{voteCount}</span>
      </div>
    );
  }

  // RENDER interactive vote button (voting active)
  const isDisabled = dotsRemaining === 0;

  return (
    <button
      className={`${styles.voteButton} ${isDisabled ? styles.voteButtonDisabled : ''}`}
      onClick={handleVote}
      disabled={isDisabled}
      title={
        isDisabled
          ? 'No dots remaining'
          : `Click to vote (${dotsRemaining} dots remaining)`
      }
    >
      <CircleDot size={14} className={styles.voteIcon} />
      <span className={styles.voteCount}>{voteCount}</span>
    </button>
  );
};
